import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import AIManagersService from 'src/services/AIManagersService';

import type { AIManagerKnowledgeDocument } from 'src/services/AIManagersService';

type LibraryState = {
    documents: AIManagerKnowledgeDocument[] | null;
    pending: boolean;
    error: string;
};
const initialState: LibraryState = { documents: null, pending: false, error: '' };

export default function useKnowledgeLibrary(scope: string, enabled: boolean) {
    const [state, setState] = useState<LibraryState>(initialState);
    const sequence = useRef(0);
    const running = useRef(false);
    const alive = useRef(false);

    const reload = useCallback(async () => {
        if (!enabled || !alive.current || running.current) return;
        const token = ++sequence.current;
        running.current = true;
        setState((current) => ({ ...current, pending: true, error: '' }));
        const isCurrent = () => alive.current && sequence.current === token;
        try {
            const documents = await AIManagersService.listAllKnowledgeDocuments();
            if (!isCurrent()) return;
            setState({
                documents: documents
                    .filter((document) => document.isActive && document.documentType === 'company_profile')
                    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
                pending: false,
                error: '',
            });
        } catch (error) {
            if (!isCurrent()) return;
            const status = (error as { response?: { status?: number } })?.response?.status;
            const denied = status === 401 || status === 403;
            setState((current) => ({
                documents: denied ? null : current.documents,
                pending: false,
                error: denied
                    ? 'Нет доступа к сохранённым базам знаний.'
                    : current.documents
                    ? 'Не удалось обновить список. Показаны ранее загруженные базы.'
                    : 'Не удалось загрузить базы знаний.',
            }));
        } finally {
            if (isCurrent()) running.current = false;
        }
    }, [enabled]);

    useLayoutEffect(() => {
        alive.current = true;
        sequence.current += 1;
        running.current = false;
        setState(initialState);
        void reload();
        return () => {
            alive.current = false;
            sequence.current += 1;
            running.current = false;
        };
    }, [scope, reload]);

    return { ...state, reload };
}
